import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  CheckCircle,
  Clock,
  HelpCircle,
  RotateCw,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MetricCards } from "@/components/metric-cards";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { EnumBadge } from "@/components/badges";
import { SearchInput } from "@/components/search-input";
import { usePatientDetail, usePatients } from "@/hooks/use-patients";
import { useDebounce } from "@/hooks/use-debounce";
import {
  caseDisplayName,
  failClassLabel,
  failClassTone,
  formatLocalDateLabel,
  formatScheduledFor,
  shortCaseId,
} from "@/lib/intake-format";
import { cn } from "@/utils";
import type { IntakeWindowCaseKpis, PatientGroup } from "@/types/intake";

const TZ = "America/New_York";

export default function PatientRegistryPage() {
  const [lookback, setLookback] = useState(7);
  const [seenMin, setSeenMin] = useState(2);
  const [seenTodayOnly, setSeenTodayOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debounced = useDebounce(searchTerm);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const {
    patients,
    summary,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
  } = usePatients({
    lookback,
    seenMin,
    seenTodayOnly,
    tz: TZ,
    pageSize: 50,
  });

  const filtered = useMemo(() => {
    const term = debounced.trim().toLowerCase();
    if (!term) return patients;
    return patients.filter((p) => {
      const name = `${p.first_name} ${p.last_name}`.toLowerCase();
      return (
        name.includes(term) ||
        (p.dob || "").includes(term) ||
        p.member_ids.some((m) => m.toLowerCase().includes(term)) ||
        p.payers.some((m) => m.toLowerCase().includes(term)) ||
        p.products.some((m) => m.toLowerCase().includes(term)) ||
        p.case_ids.some((m) => m.toLowerCase().includes(term))
      );
    });
  }, [patients, debounced]);

  const handleRefresh = useCallback(() => {
    void refresh();
  }, [refresh]);

  const emptyWindowKpis: IntakeWindowCaseKpis = useMemo(
    () => ({
      total: 0,
      completed: 0,
      not_submitted: 0,
      in_flight: 0,
      retryable: 0,
      exhausted: 0,
      no_dialable_phone: 0,
    }),
    [],
  );

  const metricItems = useMemo(() => {
    const k =
      summary?.window_patient_kpis ??
      summary?.window_case_kpis ??
      emptyWindowKpis;
    const totalLabel = summary?.window_patient_kpis
      ? "Patients (listed)"
      : "Total received";
    return [
      { label: totalLabel, value: k.total, icon: Activity },
      {
        label: "PA outcome",
        value: k.completed,
        icon: CheckCircle,
        color: "text-green-500",
      },
      {
        label: "Not submitted",
        value: k.not_submitted,
        icon: HelpCircle,
        color: "text-amber-500",
      },
      {
        label: "In flight",
        value: k.in_flight,
        icon: Clock,
        color: "text-blue-500",
      },
      {
        label: "Retryable",
        value: k.retryable,
        icon: RotateCw,
        color: "text-amber-600",
        secondary: { value: k.no_dialable_phone, label: "no phone" },
      },
      {
        label: "Exhausted",
        value: k.exhausted,
        icon: XCircle,
        color: "text-red-500",
      },
    ];
  }, [
    summary?.window_patient_kpis,
    summary?.window_case_kpis,
    emptyWindowKpis,
  ]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Patient Registry
          </h1>
          <p className="text-sm text-muted-foreground">
            Cases rolled up by patient (name + DOB) over the last{" "}
            {summary?.lookback ?? lookback} days. Repeat patients float to the
            top. KPI cards prefer each listed patient once from their most
            recent case in the window; otherwise they use case totals for the
            same window (Daily Intake buckets).
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={handleRefresh}>
          <RotateCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by name, DOB, member ID, payer, product, case…"
        />
        <div className="flex flex-col">
          <label
            htmlFor="patients-lookback"
            className="text-xs font-medium text-muted-foreground"
          >
            Lookback (days)
          </label>
          <Input
            id="patients-lookback"
            type="number"
            min={1}
            max={180}
            value={lookback}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (Number.isFinite(n)) setLookback(Math.min(180, Math.max(1, n)));
            }}
            className="h-9 w-28"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="patients-seen-min"
            className="text-xs font-medium text-muted-foreground"
          >
            Min cases
          </label>
          <Input
            id="patients-seen-min"
            type="number"
            min={1}
            max={50}
            value={seenMin}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (Number.isFinite(n)) setSeenMin(Math.min(50, Math.max(1, n)));
            }}
            className="h-9 w-24"
          />
        </div>
        <label className="flex items-center gap-2 pt-5 text-xs font-medium">
          <input
            type="checkbox"
            checked={seenTodayOnly}
            onChange={(e) => setSeenTodayOnly(e.target.checked)}
          />
          Seen today only
        </label>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <MetricCards
        metrics={metricItems}
        loading={loading && patients.length === 0}
      />

      <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
        <Users className="mr-1 inline h-3.5 w-3.5" />
        {loading
          ? "Loading patients…"
          : `${filtered.length} of ${summary?.patient_count ?? 0} patient${
              (summary?.patient_count ?? 0) === 1 ? "" : "s"
            } shown · ${summary?.case_count ?? 0} cases in window${
              summary && summary.unkeyed_case_count
                ? ` · ${summary.unkeyed_case_count} cases unkeyed (missing name or DOB)`
                : ""
            }`}
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-[920px] border-collapse">
          <thead>
            <tr className="border-b bg-muted/60 text-left text-xs font-medium text-muted-foreground">
              <th className="px-2 py-2">Patient</th>
              <th className="px-2 py-2">DOB</th>
              <th className="px-2 py-2">Cases</th>
              <th className="px-2 py-2">First seen</th>
              <th className="px-2 py-2">Last seen</th>
              <th className="px-2 py-2">Payers</th>
              <th className="px-2 py-2">Outcomes</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading && patients.length === 0
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-2 py-2">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              : filtered.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="h-24 text-center text-sm text-muted-foreground"
                    >
                      No patients match the current filters.
                    </td>
                  </tr>
                )
                : filtered.map((p) => (
                    <PatientRow
                      key={p.patient_key}
                      p={p}
                      onOpen={() => setOpenKey(p.patient_key)}
                    />
                  ))}
          </tbody>
        </table>
      </div>

      {hasMore ? (
        <div className="flex justify-center pb-2">
          <Button variant="outline" onClick={loadMore} disabled={loadingMore}>
            {loadingMore
              ? "Loading…"
              : `Load 50 more (${patients.length} of ${summary?.patient_count ?? "?"} shown)`}
          </Button>
        </div>
      ) : null}

      <PatientDetailSheet
        patientKey={openKey}
        onClose={() => setOpenKey(null)}
      />
    </div>
  );
}

function PatientRow({
  p,
  onOpen,
}: {
  p: PatientGroup;
  onOpen: () => void;
}) {
  const repeat = p.case_count > 1;
  return (
    <tr className="border-b text-xs hover:bg-muted/30">
      <td className="px-2 py-2 align-top">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">
            {[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}
          </span>
          {repeat ? (
            <span className="rounded-sm bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-800 dark:bg-purple-950/60 dark:text-purple-200">
              ×{p.case_count}
            </span>
          ) : null}
        </div>
        {p.member_ids.length > 0 ? (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {p.member_ids.slice(0, 2).join(", ")}
            {p.member_ids.length > 2 ? ` +${p.member_ids.length - 2}` : ""}
          </div>
        ) : null}
      </td>
      <td className="px-2 py-2 align-top">{p.dob || "—"}</td>
      <td className="px-2 py-2 align-top tabular-nums">{p.case_count}</td>
      <td className="px-2 py-2 align-top">
        {p.first_seen_date ? formatLocalDateLabel(p.first_seen_date) : "—"}
      </td>
      <td className="px-2 py-2 align-top">
        {p.last_seen_date ? formatLocalDateLabel(p.last_seen_date) : "—"}
      </td>
      <td className="px-2 py-2 align-top">
        {p.payers.length > 0 ? (
          <span className="line-clamp-2">
            {p.payers.slice(0, 2).join(", ")}
            {p.payers.length > 2 ? ` +${p.payers.length - 2}` : ""}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="px-2 py-2 align-top">
        <div className="flex flex-wrap gap-1">
          {p.fail_classes.slice(0, 3).map((fc) => (
            <EnumBadge
              key={fc}
              value={failClassLabel(fc)}
              semanticTone={failClassTone(fc)}
              className="text-[10px]"
            />
          ))}
          {p.fail_classes.length > 3 ? (
            <span className="text-[10px] text-muted-foreground">
              +{p.fail_classes.length - 3}
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-2 py-2 text-right align-top">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onOpen}>
          View
        </Button>
      </td>
    </tr>
  );
}

function PatientDetailSheet({
  patientKey,
  onClose,
}: {
  patientKey: string | null;
  onClose: () => void;
}) {
  const open = !!patientKey;
  const { data, loading, error } = usePatientDetail(patientKey, 90, TZ);

  const showSkeleton = open && (loading || (!data && !error));

  return (
    <Sheet open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>
            {data
              ? [data.first_name, data.last_name].filter(Boolean).join(" ") ||
                "Patient"
              : "Patient"}
          </SheetTitle>
          <SheetDescription>
            {data?.dob ? `DOB ${data.dob}` : null}
            {data
              ? ` · ${data.case_count} case${data.case_count === 1 ? "" : "s"}`
              : null}
            {!data && !loading && error ? error : null}
          </SheetDescription>
          <button
            type="button"
            aria-label="Close"
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </SheetHeader>

        {showSkeleton ? (
          <div className="space-y-2 px-1">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : null}

        {!loading && error && !data ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            Couldn’t load patient detail: {error}
          </div>
        ) : null}

        {data && data.cases.length === 0 ? (
          <div className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
            No cases found for this patient in the last 90 days.
          </div>
        ) : null}

        {data && data.cases.length > 0 ? (
          <div className="-mr-4 flex-1 overflow-y-auto pr-4">
            <ol className="relative space-y-4 border-l border-border pl-4">
              {data.cases.map((c) => (
                <li key={c.case_id}>
                  <div className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border bg-background" />
                  <div className="rounded-md border bg-muted/20 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                      <Link
                        to={`/intake?date=${encodeURIComponent(c.local_date || "")}`}
                        className="font-medium underline-offset-2 hover:underline"
                      >
                        {c.local_date
                          ? formatLocalDateLabel(c.local_date)
                          : "—"}
                      </Link>
                      <code className="font-mono text-[11px]" title={c.case_id}>
                        {shortCaseId(c.case_id)}
                      </code>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      <EnumBadge
                        value={failClassLabel(c.fail_class)}
                        semanticTone={failClassTone(c.fail_class)}
                      />
                      <span className="text-muted-foreground">
                        {caseDisplayName(c)} ·{" "}
                        {c.structured_output_payer_name || c.payer_name || "—"}
                      </span>
                    </div>
                    <div className="mt-1 grid grid-cols-2 gap-x-3 text-[11px] text-muted-foreground">
                      <span>Status: {c.status || "—"}</span>
                      <span>Attempts: {c.attempts}</span>
                      <span>
                        Member: {c.member_id || "—"} ({c.member_bin || "—"})
                      </span>
                      <span>Product: {c.product_name || "—"}</span>
                      {c.fail_reason ? (
                        <span className="col-span-2">
                          Fail reason: {c.fail_reason.replace(/_/g, " ")}
                        </span>
                      ) : null}
                      {c.pa_status ? (
                        <span className="col-span-2">
                          PA: {c.pa_status.replace(/_/g, " ")}
                        </span>
                      ) : null}
                      {c.outbound_scheduled_for ? (
                        <span
                          className="col-span-2"
                          title={`outbound_scheduled_for=${c.outbound_scheduled_for}`}
                        >
                          Next call scheduled:{" "}
                          {formatScheduledFor(c.outbound_scheduled_for)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
